import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MessageCircle, X, Sparkles, Send } from 'lucide-react';

const WELCOME_MESSAGE = {
  role: 'assistant',
  text: "Hi! I'm your BookMyDriver assistant. Ask me about bookings, payments, FASTag, safety features — anything you need help with.",
  id: 'welcome',
};

const SUGGESTED_PROMPTS = [
  'How do I cancel my booking?',
  'How does FASTag recharge work?',
  'What are the hourly rates?',
  'Is my phone number shared with drivers?',
];

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('support_session_id') || null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Load history for existing session on mount
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const { data } = await api.get('/api/support/history', { params: { session_id: sessionId } });
        if (data && data.length > 0) {
          setMessages([WELCOME_MESSAGE, ...data]);
        }
      } catch { /* ignore */ }
    })();
  }, [sessionId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = async (text) => {
    if (!text || sending) return;
    setSending(true);
    // Optimistically append user message
    const userMsg = { role: 'user', text, id: `local-${Date.now()}` };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    try {
      const { data } = await api.post('/api/support/chat', {
        message: text,
        session_id: sessionId,
      });
      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem('support_session_id', data.session_id);
      }
      setMessages((prev) => [...prev, { role: 'assistant', text: data.reply, id: `bot-${Date.now()}` }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: typeof detail === 'string' ? detail : "Sorry, I couldn't reach the assistant right now. Please try again in a moment.",
        id: `err-${Date.now()}`,
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input.trim());
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="support-chat-toggle"
          className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-primary text-white shadow-2xl hover:bg-primary-dark flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Open support chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden" data-testid="support-chat-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary-dark text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <div>
                <p className="font-semibold leading-none">Support</p>
                <p className="text-xs text-white/80 mt-1">Powered by Gemini</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              data-testid="support-chat-close"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-background">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-primary text-white rounded-br-md'
                      : m.error
                        ? 'bg-red-100 text-red-800 rounded-bl-md'
                        : 'bg-white text-foreground rounded-bl-md shadow-sm border border-border'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white text-muted-foreground px-3 py-2 rounded-2xl rounded-bl-md shadow-sm border border-border text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}

            {messages.length <= 1 && !sending && (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-2">Suggested questions:</p>
                <div className="space-y-1.5">
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => sendMessage(p)}
                      className="w-full text-left text-sm bg-white border border-border hover:border-accent hover:bg-accent/10 text-foreground px-3 py-2 rounded-lg transition-colors"
                      data-testid="support-suggested-prompt"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="border-t border-border bg-white p-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything..."
              disabled={sending}
              className="flex-1"
              data-testid="support-chat-input"
            />
            <Button
              type="submit"
              disabled={sending || !input.trim()}
              className="bg-primary hover:bg-primary-dark"
              data-testid="support-chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
